/**
 * Money entered as text, converted to minor units.
 *
 * NFR-FUNC-2 and NFR-FUNC-3: every amount is an integer in minor units plus an
 * explicit currency code, and no amount is ever a float. That rules out the
 * obvious implementation -- `Math.round(parseFloat(input) * 100)` -- because
 * `parseFloat` has already lost the value by then: `19.99 * 100` is
 * `1998.9999999999998`, and `1.005 * 100` rounds the wrong way.
 *
 * So the digits are read as digits and never multiplied.
 */
import type { Money } from '@intentionalist/shared';
import { currencyDisplay } from './currency-display.js';

/**
 * Two, for every currency this handles today.
 *
 * Currencies differ -- JPY has none, KWD has three -- so this constant is only
 * safe while every stored amount is in the one currency below. The device
 * currency setting deliberately does not widen that: it chooses whether the
 * code is *shown*, not what an amount is denominated in. A second currency
 * means this becomes a property of the currency, and the setting is shaped as a
 * list so that day changes a table rather than a type.
 */
const MINOR_DIGITS = 2;

/**
 * What a newly entered amount is denominated in.
 *
 * Not the device setting. NFR-FUNC-3 requires every persisted amount to carry a
 * code, so there is no such thing as storing an amount with no currency, and
 * "None" in the settings cannot mean that -- it means the code is not printed.
 * Amounts are stored as USD whichever way the setting is pointed, which is what
 * keeps the setting reversible: turning it on and off changes the rendering and
 * never the data.
 */
export const DEFAULT_CURRENCY = 'USD';

/**
 * Parses an amount, or returns null if it is not one.
 *
 * Accepts grouping separators and a leading currency symbol, because people paste
 * what they see on a receipt. Rejects anything negative: no field in the model
 * takes one, and silently dropping the sign would be worse than refusing.
 */
export function parseMoney(input: string, currency: string = DEFAULT_CURRENCY): Money | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  const cleaned = trimmed.replace(/^[^\d.,-]+/, '').replace(/,/g, '');
  if (!/^\d*(\.\d*)?$/.test(cleaned) || cleaned === '' || cleaned === '.') return null;

  const [whole = '', fraction = ''] = cleaned.split('.');
  if (fraction.length > MINOR_DIGITS) return null;

  // Padded as a string, so the digits are never routed through a float.
  const minor = Number(`${whole || '0'}${fraction.padEnd(MINOR_DIGITS, '0')}`);
  if (!Number.isSafeInteger(minor)) return null;

  return { minor, currency };
}

/**
 * Renders minor units as digits, without arithmetic on the fractional part.
 *
 * Bare, with no currency code, because this is also what goes back into an
 * input field. `formatAmount` is the one for display.
 */
export function formatMoney(money: Money | null | undefined): string {
  if (money === null || money === undefined) return '';
  const digits = String(money.minor).padStart(MINOR_DIGITS + 1, '0');
  const whole = digits.slice(0, -MINOR_DIGITS);
  const fraction = digits.slice(-MINOR_DIGITS);
  return `${whole}.${fraction}`;
}

/**
 * An amount as a reader sees it (FR-2.18).
 *
 * The device setting decides whether the code is shown, with one override. A
 * screen that lists a total per currency is already relying on the code to tell
 * them apart, so it passes `ambiguous` and gets the code regardless: a column
 * reading "120.00 · 95.00" is not a shorter rendering of the same information,
 * it is a worse one. Everywhere else a single catalog currency is a safe
 * assumption and the code is noise the setting exists to remove.
 */
export function formatAmount(
  money: Money | null | undefined,
  options: { ambiguous?: boolean } = {},
): string {
  if (money === null || money === undefined) return '';
  const digits = formatMoney(money);
  const withCode = options.ambiguous === true || currencyDisplay() !== 'none';
  return withCode ? `${digits} ${money.currency}` : digits;
}

/**
 * What goes back into a form field, so an edit round-trips unchanged.
 *
 * Stays bare even when the setting shows codes, because `parseMoney` strips a
 * leading symbol and not a trailing code -- a field seeded with "75.00 USD"
 * would refuse to save the value it was just handed.
 */
export const moneyToInput = (money: Money | null | undefined): string => formatMoney(money);
